/**
 * Final Ad Slide
 *
 * Slide 5: The big reveal! Shows the generated ad and "Get Started" button.
 */

import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { Rocket, Play, ArrowRight, Sparkles } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import type { OnboardingArtifacts } from '../../../contexts/OnboardingContext'

interface FinalAdSlideProps {
  artifacts: OnboardingArtifacts
  onComplete: () => void
}

// Simple confetti particle component
function ConfettiParticle({ delay, color }: { delay: number; color: string }) {
  const randomX = Math.random() * 100
  const randomDuration = 2 + Math.random() * 2

  return (
    <motion.div
      initial={{ y: -20, x: `${randomX}vw`, opacity: 1, rotate: 0 }}
      animate={{
        y: '100vh',
        rotate: 360 + Math.random() * 360,
        opacity: [1, 1, 0],
      }}
      transition={{
        duration: randomDuration,
        delay,
        ease: 'linear',
      }}
      className={`absolute w-3 h-3 ${color}`}
      style={{ left: 0, top: 0 }}
    />
  )
}

const CONFETTI_COLORS = [
  'bg-rose-400',
  'bg-blue-400',
  'bg-emerald-400',
  'bg-amber-400',
  'bg-purple-400',
  'bg-pink-400',
]

export default function FinalAdSlide({ artifacts, onComplete }: FinalAdSlideProps) {
  const winner = artifacts.winner
  const [showConfetti, setShowConfetti] = useState(true)

  // Stop confetti after a few seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 4000)
    return () => clearTimeout(timer)
  }, [])

  const videoUrl = winner?.video_url
  const thumbnailUrl = winner?.thumbnail_url
  const copy = winner?.copy
  const angle = winner?.angle

  return (
    <div className="flex flex-col items-center text-center relative">
      {/* Confetti */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
          {Array.from({ length: 50 }).map((_, i) => (
            <ConfettiParticle
              key={i}
              delay={i * 0.05}
              color={CONFETTI_COLORS[i % CONFETTI_COLORS.length]}
            />
          ))}
        </div>
      )}

      {/* Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: [0, 1.2, 1] }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="w-20 h-20 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center mb-8 shadow-lg"
      >
        <Rocket className="w-10 h-10 text-white" />
      </motion.div>

      {/* Title */}
      <motion.h2
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-3xl font-bold text-tf-text mb-2"
      >
        Your Ad is Ready!
      </motion.h2>

      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-tf-text-muted mb-8"
      >
        Here's what we created for you
      </motion.p>

      {/* Ad preview */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="w-full max-w-sm mb-8"
      >
        <div className="relative rounded-2xl overflow-hidden bg-tf-bg-muted border border-tf-border shadow-xl">
          {/* Video/Thumbnail */}
          {videoUrl ? (
            <div className="relative aspect-[9/16] bg-black">
              <video
                src={videoUrl}
                poster={thumbnailUrl}
                className="w-full h-full object-cover"
                controls
                playsInline
              />
            </div>
          ) : thumbnailUrl ? (
            <div className="relative aspect-[9/16]">
              <img
                src={thumbnailUrl}
                alt="Ad preview"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                  <Play className="w-8 h-8 text-tf-text ml-1" />
                </div>
              </div>
            </div>
          ) : (
            <div className="aspect-[9/16] flex items-center justify-center">
              <Sparkles className="w-12 h-12 text-tf-text-muted" />
            </div>
          )}

          {/* Copy preview */}
          {copy && (
            <div className="p-4 border-t border-tf-border">
              {angle && (
                <span className="inline-block px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium mb-2">
                  {angle}
                </span>
              )}
              <p className="text-sm text-tf-text font-medium line-clamp-2">
                {copy.headline}
              </p>
              <p className="text-xs text-tf-text-muted mt-1 line-clamp-2">
                {copy.primary_text}
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* CTA Button */}
      <motion.button
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.8 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onComplete}
        className={cn(
          'flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-white',
          'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700',
          'shadow-lg hover:shadow-xl transition-all'
        )}
      >
        Get Started
        <ArrowRight className="w-5 h-5" />
      </motion.button>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="mt-4 text-sm text-tf-text-muted"
      >
        Explore the full marketing suite and create more ads
      </motion.p>
    </div>
  )
}
